import { Injectable, NgZone } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { AuthService } from './auth.service';
import { SiteService } from './site.service';
import { PlatformService } from './platform.service';
import { environment } from '../../../environments/environment';
import { HEARTBEAT_POLL_MS } from '../constants/session-lifecycle.config';
import { messageForSessionEnd } from '../utils/session-expiry-message';

/**
 * Heartbeat — periodic liveness check of the current session, app-wide
 * (admin + public), 2026-07-20.
 *
 * While the tab is visible, polls the existing `/auth/me` endpoint every 5
 * minutes (see AuthService.loadCurrentUser(), which already uses this exact
 * endpoint for boot-time restore — reused here rather than adding any new
 * backend route). `/auth/me` is the right choice for this: it's already a
 * cheap, existing, auth-required GET that returns 401/403 the moment the
 * session is no longer valid server-side (revoked token, rotated secret,
 * admin-forced logout) even if the access token hasn't locally expired yet —
 * exactly the gap AuthService's own JWT-expiry-driven health monitor
 * (startTokenHealthMonitor/checkAndRefreshToken) cannot see, since that one
 * only acts on the token's own `exp` claim and never round-trips the server
 * in between refreshes.
 *
 * Respects visibilitychange: does not poll a backgrounded/hidden tab (no
 * point spending a request on a tab nobody is looking at, and it matches
 * VersionWatchdogService's existing convention of treating tab visibility as
 * the gate for background work).
 *
 * On auth failure: this is deliberately NOT the same path as IdleService's
 * forced sign-out. A heartbeat failure means the session is already dead
 * server-side RIGHT NOW, while the user may be mid-form — exactly the
 * 2026-07-17 dead-session incident's shape. The correct response, per that
 * incident's fix, is the EXISTING in-place recovery: emitSessionExpired()
 * (any component's dirty-form/upload guard reacts first — same signal
 * project-registration's helpers/auth.helpers.ts already listens for),
 * clearAuthToken() (the session really is dead — mirrors what
 * auth.interceptor.ts's handleSessionExpired does before its own
 * now-legacy reload-or-redirect branch), then requestLoginModal() so the
 * header's sign-in modal (or, on admin routes, the chromeless
 * `<app-header [loginOnly]="true">` added to AdminLayoutComponent in this
 * PR) opens ON TOP of the current page. No navigation, no reload — the page
 * underneath is untouched and resumes the instant the user signs back in
 * (see HeaderComponent.submitLogin's loginIsSessionRecovery branch, which
 * already skips its normal post-login redirect for exactly this case).
 */
@Injectable({ providedIn: 'root' })
export class HeartbeatService {
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private started = false;
  private boundOnVisibility = () => this.onVisibilityChange();
  private inFlight = false;

  constructor(
    private zone: NgZone,
    private http: HttpClient,
    private authService: AuthService,
    private siteService: SiteService,
    private platformService: PlatformService,
  ) {}

  start(): void {
    // A tab-visibility poll has no meaning on the server — one-shot render,
    // no tab. `document` is not a global there either.
    if (!this.platformService.isBrowser) return;
    if (this.started) return;
    this.started = true;
    this.zone.runOutsideAngular(() => {
      this.pollTimer = setInterval(() => this.tick(), HEARTBEAT_POLL_MS);
      document.addEventListener('visibilitychange', this.boundOnVisibility);
    });
  }

  stop(): void {
    this.started = false;
    if (this.pollTimer) { clearInterval(this.pollTimer); this.pollTimer = null; }
    document.removeEventListener('visibilitychange', this.boundOnVisibility);
  }

  /** Returning to a backgrounded tab checks immediately, same convention as
   *  AuthService.onVisibilityChange / VersionWatchdogService.start(). */
  private onVisibilityChange(): void {
    if (document.visibilityState === 'visible') this.zone.run(() => this.tick());
  }

  private tick(): void {
    if (document.visibilityState !== 'visible') return; // never poll a hidden tab
    if (this.inFlight) return; // coalesce — don't stack calls if one is slow
    // Louisville regression 2026-07-20 (same launch day): the original guard
    // here was hasUserSession(), which is true for LEFTOVER localStorage
    // markers (project_user_session / lp_permit_token) from any long-dead
    // login — so PUBLIC visitors with stale keys got heartbeat-polled, the
    // guaranteed 401 tore down their (free-token) auth state mid-form, and
    // the directory register page surfaced "Something went wrong". Only a
    // LIVE user object justifies a session heartbeat; stale-marker cleanup
    // happens lazily below when a poll was already in flight for one.
    if (!this.authService.isUserLoggedIn()) {
      this.reconcileStaleSessionMarkers();
      return;
    }
    const token = this.authService.getToken();
    if (!token) return;

    this.inFlight = true;
    this.http.get<any>(`${environment.apiUrl}/auth/me`, {
      withCredentials: true,
      headers: new HttpHeaders({ Authorization: `Bearer ${token}` }),
    }).subscribe({
      next: () => { this.inFlight = false; },
      error: (err: HttpErrorResponse) => {
        this.inFlight = false;
        const status = err?.status ?? 0;
        if (status === 401 || status === 403) {
          this.recoverInPlace(err);
        }
        // Network/5xx: transient — say nothing, the next poll (or the next
        // real API call, via the interceptor) will sort it out. Matches
        // AuthService.checkAndRefreshToken's existing transient-vs-definitive
        // split.
      },
    });
  }

  /** The in-place dead-session recovery signal, extended to every route
   *  (previously only reachable from /incentive's own component wiring).
   *  Guarded to LIVE logged-in users only — for anyone else there is no
   *  session to recover, and clearing tokens/raising the modal breaks a
   *  public visitor's free-token browsing (the 2026-07-20 Louisville
   *  register regression). */
  private recoverInPlace(err: HttpErrorResponse): void {
    if (!this.authService.isUserLoggedIn()) {
      this.reconcileStaleSessionMarkers();
      return;
    }
    this.authService.emitSessionExpired();
    this.authService.clearAuthToken();
    this.authService.requestLoginModal({ message: messageForSessionEnd(err) });
  }

  /** A visitor with NO live user but leftover session markers in
   *  localStorage (from a long-expired login) is wedged: hasUserSession()
   *  keeps getToken() from ever falling back to the free token, so public
   *  API calls go out unauthenticated and fail. Clear the stale markers and
   *  refresh the free token so public browsing works — quietly, with no
   *  modal (there is no session to sign back in to; they were never
   *  logged in this visit). */
  private reconcileStaleSessionMarkers(): void {
    if (!this.authService.hasUserSession()) return; // nothing stale — plain anonymous visitor
    this.authService.clearAuthToken();
    this.siteService.refreshFreeToken().subscribe({ next: () => {}, error: () => {} });
  }
}
